// The thread, as the screen rather than as a section of it. Lifted from the
// Andon app's `ThreadView`, minus the follow-up tasks.
//
// Everything that was a stacked card — what the operator said, the answers,
// the photo, who was notified, the acknowledgement — is a message here,
// oldest at the top, newest at the bottom, scrolled to the bottom on open.
// That is the change: an andon stops being a status page with a comment box
// under it and becomes a conversation with a status strip over it.
//
// TWO KINDS OF ENTRY, DRAWN TWO WAYS
//
//   message   a bubble with a side. Whose side is about this device's person
//             — the employee on a PIN device, the user on a personal sign-in.
//
//             AND THE RAISE IS ONE OF THEM. `kind: 'raise'` is the operator's
//             opening bubble: the question above every answer, then their
//             words, then their photos and clips — one bubble, from them, at
//             the top. A MESSAGE AND NOT A PANEL, because it is the thing
//             somebody said: a panel above the thread is chrome the eye learns
//             to skip. Drawn as a bubble it inherits everything bubbles do —
//             one-tap reactions, the exact time one tap away, and selectable
//             text, so a part number goes straight into a purchase order.
//   event     a line across the middle. "Ben took it · here in 2 min" in a
//             speech bubble attributed to a person is how one thread became
//             unskimmable. The lead is in ink and the circumstance in sub, so
//             a glance gets the subject and the verb, and an escalation
//             carries the amber it already means.
//
// AND A MESSAGE CAN BE ANSWERED WITHOUT A MESSAGE.
//
// Every bubble carries an emoji row, and that is not decoration on a factory
// tool. The commonest reply on a stopped line is "seen" — and the version of
// it that costs a whole line of thread is the version people stop sending, so
// the operator stops knowing whether anybody read "on my way, two minutes". A
// tap says it, and the person who WROTE the message is told: one push, to the
// author, not to the whole ladder.
//
// WHAT THE LIFT DROPPED. The task bubbles — "Priya, re-order eight" with a
// box to tick — went with the follow-ups, which the merge decided against.
// An instance still running the older addon sends no events at all and its
// messages come from the chatter, exactly as before.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { andonApi, andonAvatarUrl } from '@/api/andon/client';
import type { AlertEventVM, QuestionsVM, ThreadAttachmentVM, ThreadMessageVM } from '@/api/andon/types';
import { ApiError } from '@/api/http';
import { Icon } from '@/components/ui/Icon';
import { useTick } from '@/hooks/useTick';
import { useT } from '@/i18n/useT';
import { clipLength, fileSize } from '@/lib/capture';
import { selectingWithin } from '@/lib/selection';
import { ago, clockTime, dayLabel, endsRun, startsRun, toEpoch } from '@/lib/thread';
import { andonKeys } from '@/queries/andon';
import { useStore } from '@/store';
import { EmojiPicker } from './EmojiPicker';
import { Lightbox } from './Lightbox';
import { OwnerAvatar } from './primitives';

/**
 * One thing in the thread, with a comparable time against it.
 *
 * `ts` is epoch ms, and everything is sorted on it rather than on the Odoo
 * datetime string, so a pending bubble — whose stamp this client wrote — and a
 * server event sort into one run.
 */
type Entry = { kind: 'message'; ts: number; message: ThreadMessageVM } | { kind: 'event'; ts: number; event: AlertEventVM };

export function ThreadView({
  alertId,
  messages,
  events = [],
  questions,
  raisedBy,
  hideAsked = false,
}: {
  /** Scopes every attachment URL. The server checks it again. */
  alertId: number;
  messages: ThreadMessageVM[];
  events?: AlertEventVM[];
  /** The raise's questions and answers, drawn at the top of the raiser's bubble. */
  questions: QuestionsVM;
  raisedBy: string;
  /**
   * Leave the intake answers out of the raise bubble — the caller has them.
   *
   * The before-you-walk-over face draws what the operator was asked directly
   * under what they said, so repeating it inside their bubble twenty pixels
   * lower would be the same four sentences twice on a phone held while
   * walking.
   */
  hideAsked?: boolean;
}) {
  const t = useT();
  const bottom = useRef<HTMLDivElement>(null);
  const now = useTick();
  /**
   * THE TAPPED MESSAGE — one at a time, or none.
   *
   * A tap on a bubble is the thread's one gesture, and it buys two things at
   * once: the exact time under EVERY bubble, and the react key beside THIS
   * one. Both are the same request — "I am working on this thread rather than
   * glancing at it" — so they are the same piece of state rather than two
   * toggles a gloved thumb has to find separately.
   *
   * Tapping a DIFFERENT bubble moves the key and leaves the times up: the
   * times are a property of the thread, the key is a property of the message
   * under the thumb.
   */
  const [openFor, setOpenFor] = useState<number | null>(null);
  const showTimes = openFor !== null;
  const qc = useQueryClient();
  const showError = useStore((st) => st.showError);

  /**
   * One mutation for the whole thread, not one per bubble.
   *
   * A thread is eighty messages at the worst of it, and a mutation per bubble
   * would be eighty subscriptions all redrawing when any one of them settles.
   * The message id travels in the payload instead.
   *
   * IT TOGGLES, and the reply is deliberately NOT used to patch the chip. The
   * thread is re-read, because a reaction is a change to a message already in
   * it and the count belongs to the server.
   */
  const react = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: number; emoji: string }) => andonApi.react(alertId, messageId, emoji),
    onSuccess: () => void qc.invalidateQueries({ queryKey: andonKeys.alert(alertId) }),
    onError: (err: unknown) => showError(err instanceof ApiError ? err.message : t.threadReactionFailedT),
  });

  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = [
      ...messages.map((m): Entry => ({ kind: 'message', ts: toEpoch(m.at), message: m })),
      ...events.map((e): Entry => ({ kind: 'event', ts: toEpoch(e.at), event: e })),
    ];
    // Stable within a tie: two things recorded in the same second keep the
    // order they were given in.
    return out
      .map((e, i) => ({ e, i }))
      .sort((a, b) => a.e.ts - b.e.ts || a.i - b.i)
      .map(({ e }) => e);
  }, [messages, events]);

  // Newest at the bottom means the bottom is where the conversation is. A
  // thread that opens at the top asks somebody walking to a stopped station to
  // scroll before they can read the thing that just happened.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [entries.length]);

  /** The same list, as `lib/thread` reads it: a time and who said it. */
  const timed = useMemo(() => entries.map((e) => ({ ts: e.ts, author: e.kind === 'message' ? e.message.author : null })), [entries]);
  const agoWords = useMemo(() => ({ justNow: t.threadJustNowT, minAgo: t.threadMinAgoT }), [t]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: '0 20px 6px' }}>
      <Divider text={`${raisedBy} ${t.threadRaisedThisT}`} />

      {entries.map((entry, i) => {
        const day = dayLabel(entry.ts, i === 0 ? null : entries[i - 1]!.ts);
        // A bubble that has not reached the server yet says so rather than
        // claiming a time. "just now" on something still in flight is the one
        // reading that would be actively wrong.
        const stamp =
          entry.kind === 'message' && entry.message.pending
            ? uploadingLabel(entry.message.uploading, t)
            : entry.kind === 'message' && (showTimes || endsRun(timed, i))
              ? showTimes
                ? clockTime(entry.ts)
                : ago(entry.ts, now, agoWords)
              : null;
        return (
          <Fragment key={`${entry.kind}-${i}`}>
            {day && <Divider text={day} />}
            {entry.kind === 'event' ? (
              <Event event={entry.event} />
            ) : (
              <Bubble
                alertId={alertId}
                m={entry.message}
                asked={hideAsked ? [] : questions.asked}
                stamp={stamp}
                lead={startsRun(timed, i)}
                revealed={openFor === entry.message.id}
                onTap={() => setOpenFor((cur) => (cur === entry.message.id ? null : entry.message.id))}
                onReact={(emoji) => react.mutate({ messageId: entry.message.id, emoji })}
                reacting={react.isPending}
              />
            )}
          </Fragment>
        );
      })}

      <div ref={bottom} />
    </div>
  );
}

/**
 * What a pending bubble's stamp says.
 *
 * "Sending…" when the whole message is in flight, and the count when the words
 * have gone and only the files are still climbing — an ordinary state, because
 * Send does not wait for uploads. A responder reading their own bubble should
 * be able to tell "this has not left the phone" from "they have it, the video
 * is coming".
 */
function uploadingLabel(uploading: number | undefined, t: ReturnType<typeof useT>): string {
  if (!uploading) return t.threadSendingT;
  return `${t.threadSendingT} · ${uploading} ${uploading === 1 ? t.threadFileStillUploadingT : t.threadFilesStillUploadingT}`;
}

/**
 * The intake answers, question above answer.
 *
 * Drawn in two places from one definition: the top of the raiser's own bubble,
 * and — on the before-you-walk-over face, where the operator's own words are
 * the first thing on the screen — its own section directly under them.
 *
 * Spans throughout, because one of those two homes is inside a bubble's
 * paragraph and a <div> there is not markup a browser will keep.
 */
export function AskedAnswers({ asked }: { asked: Array<{ question: string; answer: string }> }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {asked.map((qa) => (
        <span key={qa.question} style={{ display: 'block' }}>
          <span style={{ display: 'block', font: '500 10.5px/1.4 Manrope,sans-serif', color: 'var(--sub)' }}>{qa.question}</span>
          <span style={{ display: 'block', font: '700 12.5px/1.45 Manrope,sans-serif', color: 'var(--ink)' }}>{qa.answer}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * One person's message. The side says whether it was this device's.
 *
 * The whole bubble is the tap target for revealing times — not a small control
 * beside it. There is nothing else to do to a message, so there is nothing for
 * the tap to be confused with, and a gloved thumb gets the whole bubble rather
 * than a twelve-pixel affordance.
 */
function Bubble({
  alertId,
  m,
  asked,
  stamp,
  lead,
  revealed,
  onTap,
  onReact,
  reacting,
}: {
  alertId: number;
  m: ThreadMessageVM;
  /**
   * What the raiser was asked and what they said, for `kind: 'raise'` only.
   *
   * Passed in rather than carried on the message, because the answers live on
   * the ALERT and copying them into the message body as well would put the
   * same four sentences in two tables and let them disagree.
   */
  asked: Array<{ question: string; answer: string }>;
  /** The time to show under it, or null while this one stays quiet. */
  stamp: string | null;
  /** First of a run — the one that carries the avatar and the name. */
  lead: boolean;
  /** This is the tapped bubble: its react key stays out without a pointer. */
  revealed: boolean;
  onTap: () => void;
  onReact: (emoji: string) => void;
  /** Something is in flight for this thread — the row goes inert, not away. */
  reacting: boolean;
}) {
  const t = useT();
  const time = stamp && (
    <span className="numeric" style={{ display: 'block', marginTop: 4, font: '600 9.5px Manrope,sans-serif', color: 'var(--sub)', textAlign: m.mine ? 'right' : 'left' }}>
      {stamp}
    </span>
  );

  // A message may be files with nothing typed under them — the commonest thing
  // a gloved hand sends. Drawing the bubble anyway would put an empty rounded
  // rectangle under every photo.
  const words = m.body.trim();
  const files = m.attachments.length > 0 && <Attachments alertId={alertId} items={m.attachments} mine={m.mine} />;

  /**
   * THE DECISION, badged and outlined.
   *
   * An event is a line the app writes ABOUT ITSELF (took it, escalated); a
   * decision is a person telling other people what they did. The green a
   * close wears, because this is the sentence claiming the andon is fixed. A
   * badge rather than a different bubble shape, so it keeps everything a
   * bubble already does.
   */
  const decisionBadge = m.kind === 'decision' && (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        marginBottom: 6,
        padding: '3px 8px',
        borderRadius: 6,
        background: 'var(--soft)',
        border: '1px solid var(--pos)',
        font: "700 8.5px 'Space Grotesk',sans-serif",
        letterSpacing: '.11em',
        color: 'var(--pos)',
        textTransform: 'uppercase',
      }}
    >
      <Icon name="check" size={10} strokeWidth={3.2} />
      {t.threadTheDecisionT}
    </span>
  );

  /** Spread onto the words bubble on whichever side it lands. */
  const decisionOutline = m.kind === 'decision' ? { border: '1.5px solid var(--pos)', background: 'var(--soft)' } : null;

  /**
   * WHAT I WAS ASKED — the block at the top of the raiser's own bubble.
   *
   * The question above every answer, because the answers on their own are
   * unreadable: "8" and "Both — not there" mean nothing without the sentence
   * they replied to. Only on the raise bubble, and only when there is
   * something to draw.
   */
  const askedBlock = m.kind === 'raise' && asked.length > 0 && (
    <span
      className="selectable"
      style={{
        display: 'block',
        marginBottom: words || m.attachments.length ? 7 : 0,
        padding: '10px 12px',
        borderRadius: 14,
        background: 'var(--surface)',
        border: '1px solid var(--line)',
      }}
    >
      <span style={{ display: 'block', font: "700 8.5px 'Space Grotesk',sans-serif", letterSpacing: '.11em', color: 'var(--sub)', marginBottom: 7, textTransform: 'uppercase' }}>
        {t.threadWhatIWasAskedT}
      </span>
      <AskedAnswers asked={asked} />
    </span>
  );

  /**
   * Drawn ahead of the server, and drawn as such.
   *
   * The whole trade in lib/optimistic.ts is that the bubble appears on the
   * tap and the HONESTY lives in how it looks — so this is the honesty. It
   * is dimmed, its stamp says Sending, and it carries no reaction row: the
   * id is negative, nothing on the server answers to it, and offering a chip
   * that would be refused is exactly the sort of button this app does not
   * draw.
   */
  const inFlight = m.pending === true || m.id < 0;

  const [picking, setPicking] = useState(false);
  const chips = m.reactions ?? [];
  const myEmoji = chips.filter((r) => r.mine).map((r) => r.emoji);

  /**
   * THE REACT KEY, OUT OF THE FLOW ENTIRELY.
   *
   * It is absolutely positioned against the bubble and takes no layout space
   * at all: nothing moves when it appears, and nothing is reserved for it
   * when it is away. It arrives on hover where there is a pointer, and on a
   * tap of the bubble where there is not — the same tap that already brings
   * up the times. A ghost chip under every bubble was eighty rows of empty
   * affordance on a thread long enough to matter.
   */
  const hasBody = Boolean(words) || m.attachments.length > 0 || asked.length > 0;
  const reactKey = !inFlight && hasBody && (
    <button
      type="button"
      className={`press reactKey${revealed ? ' on' : ''}`}
      aria-label={t.threadReactT}
      aria-haspopup="dialog"
      onClick={() => setPicking(true)}
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        // Outside the bubble on whichever side the bubble is not: your own
        // messages hang it to their left, everybody else's to their right.
        ...(m.mine ? { left: -34 } : { right: -34 }),
        width: 28,
        height: 28,
        borderRadius: 14,
        background: 'var(--surface2)',
        border: '1px solid var(--line)',
        color: 'var(--sub)',
        display: 'grid',
        placeItems: 'center',
        cursor: 'pointer',
      }}
    >
      <Icon name="smile" size={14} strokeWidth={1.8} />
    </button>
  );

  const picker = (
    <EmojiPicker
      open={picking}
      onClose={() => setPicking(false)}
      onPick={(emoji) => {
        setPicking(false);
        onReact(emoji);
      }}
      title={t.threadReactT}
      chosen={myEmoji}
    />
  );

  const wordsBubble = (side: 'mine' | 'theirs') =>
    words && (
      <span
        className="selectable"
        role="button"
        tabIndex={0}
        // A drag across a part number somebody typed is a selection, not a
        // tap — revealing the timestamps under it is not what was asked.
        onClick={(e) => {
          if (selectingWithin(e.currentTarget)) return;
          onTap();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onTap();
          }
        }}
        style={{
          display: side === 'theirs' ? 'block' : undefined,
          maxWidth: '100%',
          marginTop: m.attachments.length > 0 ? 4 : 0,
          padding: '11px 14px',
          borderRadius: side === 'mine' ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
          background: side === 'mine' ? 'var(--surface2)' : 'var(--surface)',
          border: side === 'mine' ? undefined : '1px solid var(--line)',
          font: '500 13.5px/1.5 Manrope,sans-serif',
          color: 'var(--ink)',
          cursor: 'pointer',
          ...decisionOutline,
        }}
      >
        {words}
      </span>
    );

  if (m.mine) {
    return (
      <div className="msgRow" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', opacity: inFlight ? 0.6 : 1, transition: 'opacity .2s ease' }}>
        {decisionBadge}
        {/* The positioning context for the react key: it hugs the widest of
            the answers, the files and the words. The 78% cap lives here — a
            percentage resolves against the containing block, and down on the
            words it would compound into a narrower bubble every time. */}
        <span style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '78%' }}>
          {askedBlock}
          {files}
          {wordsBubble('mine')}
          {reactKey}
        </span>
        {!inFlight && chips.length > 0 && <Chips m={m} chips={chips} onReact={onReact} busy={reacting} />}
        {time}
        {picker}
      </div>
    );
  }
  return (
    <div className="msgRow" style={{ display: 'flex', gap: 9, opacity: inFlight ? 0.6 : 1, transition: 'opacity .2s ease' }}>
      {/* The avatar column is held open even when it is empty, so a run stays
          in one vertical line instead of the second message sliding left. */}
      {lead ? (
        <span style={{ marginTop: 2, display: 'block' }}>
          <OwnerAvatar
            name={m.author}
            size={28}
            round
            background="var(--pos)"
            color="#fff"
            photoUrl={andonAvatarUrl({ uid: m.authorUid, employeeId: m.authorEmployeeId })}
          />
        </span>
      ) : (
        <span aria-hidden="true" style={{ width: 28, flex: 'none' }} />
      )}
      <span style={{ maxWidth: '78%' }}>
        {lead && <span style={{ display: 'block', font: '600 10px Manrope,sans-serif', color: 'var(--sub)', margin: '0 0 4px 3px' }}>{m.author}</span>}
        {decisionBadge}
        <span style={{ position: 'relative', display: 'block' }}>
          {askedBlock}
          {files}
          {wordsBubble('theirs')}
          {reactKey}
        </span>
        {!inFlight && chips.length > 0 && <Chips m={m} chips={chips} onReact={onReact} busy={reacting} />}
        {time}
        {picker}
      </span>
    </div>
  );
}

/**
 * The emoji chips under one bubble — what people actually reacted with.
 *
 * DRAWN ONLY WHEN THERE ARE SOME. Chips are the quietest thing on the bubble:
 * 26px, no border until somebody has reacted, and the subdued ink. A run of
 * them down a thread must read as texture rather than as a row of buttons.
 *
 * The row sits on the same side as the bubble, so a run of your own messages
 * keeps its right edge and somebody else's keeps its left.
 */
function Chips({
  m,
  chips,
  onReact,
  busy,
}: {
  m: ThreadMessageVM;
  chips: NonNullable<ThreadMessageVM['reactions']>;
  onReact: (emoji: string) => void;
  busy: boolean;
}) {
  const t = useT();
  return (
    <span
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        marginTop: 5,
        justifyContent: m.mine ? 'flex-end' : 'flex-start',
        // Inert rather than hidden while a tap is in flight: a row that
        // disappears under a thumb is a row somebody taps twice.
        opacity: busy ? 0.55 : 1,
        pointerEvents: busy ? 'none' : undefined,
      }}
    >
      {chips.map((r) => (
        <button
          key={r.emoji}
          type="button"
          className="press"
          // The names, for anybody who wants to know who. The label says the
          // number, the title says the people, and on a busy andon the server
          // caps the list.
          title={r.who.join(', ')}
          aria-label={`${r.emoji} ${r.count}${r.mine ? `, ${t.threadIncludingYouT}` : ''}`}
          aria-pressed={r.mine}
          onClick={() => onReact(r.emoji)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            height: 26,
            padding: '0 8px',
            borderRadius: 13,
            background: r.mine ? 'var(--soft)' : 'var(--surface2)',
            border: `1px solid ${r.mine ? 'var(--accent)' : 'transparent'}`,
            color: 'var(--ink)',
            font: '600 11px Manrope,sans-serif',
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 13 }}>
            {r.emoji}
          </span>
          <span className="numeric" style={{ color: r.mine ? 'var(--accent)' : 'var(--sub)' }}>
            {r.count}
          </span>
        </button>
      ))}
    </span>
  );
}

/**
 * The files on one message, drawn IN it.
 *
 * The rule everything below follows: show the thing, not a link to the thing.
 * A responder deciding whether to walk over needs to see the empty rack, and a
 * row saying "rack-empty.jpg — tap to view" makes that a decision about whether
 * to spend a tap finding out.
 *
 * NOTHING HERE LOADS UNTIL IT IS ON SCREEN. Every element is native — `<img>`,
 * `<video>`, `<audio>` — with `preload="none"` or `loading="lazy"`, because the
 * browser's own ranged, resumable, cache-aware loading beats anything
 * hand-written here, and a thread with six clips in it must not fetch six clips
 * to draw one screen.
 */
function Attachments({ alertId, items, mine }: { alertId: number; items: ThreadAttachmentVM[]; mine: boolean }) {
  const t = useT();
  const [viewing, setViewing] = useState<ThreadAttachmentVM | null>(null);
  const pictures = items.filter((a) => a.kind === 'photo');

  return (
    <>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: '100%', alignItems: mine ? 'flex-end' : 'flex-start' }}>
        {items.map((file) =>
          file.kind === 'photo' ? (
            <button
              key={file.id}
              type="button"
              // A picture opens; it does not toggle the timestamps. Tapping a
              // photo to see it bigger is the one thing everybody expects a
              // photo in a thread to do.
              onClick={() => setViewing(file)}
              aria-label={`${t.openT} ${file.name}`}
              style={{ padding: 0, border: 'none', background: 'none', borderRadius: 14, overflow: 'hidden', cursor: 'zoom-in', lineHeight: 0, maxWidth: '100%' }}
            >
              <img
                src={andonApi.attachmentUrl(alertId, file.id)}
                alt={file.name}
                loading="lazy"
                // Bounded rather than natural size: a portrait phone photo at
                // its own aspect ratio takes the entire thread height.
                style={{ display: 'block', maxWidth: 240, maxHeight: 280, width: 'auto', height: 'auto', objectFit: 'cover', background: 'var(--surface)' }}
              />
            </button>
          ) : file.kind === 'video' ? (
            <video
              key={file.id}
              src={andonApi.attachmentUrl(alertId, file.id)}
              controls
              playsInline
              // Nothing fetched until somebody presses play. The server serves
              // Range requests, so pressing play seeks rather than downloading
              // the whole clip first.
              preload="none"
              style={{ display: 'block', maxWidth: 260, maxHeight: 300, borderRadius: 14, background: 'var(--surface2)' }}
            />
          ) : file.kind === 'voice' ? (
            <span
              key={file.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                padding: '9px 13px 9px 11px',
                borderRadius: 16,
                background: mine ? 'var(--surface2)' : 'var(--surface)',
                border: mine ? 'none' : '1px solid var(--line)',
                maxWidth: 250,
              }}
            >
              <Icon name="mic" size={16} />
              <audio src={andonApi.attachmentUrl(alertId, file.id)} controls preload="none" style={{ height: 30, maxWidth: 170 }} />
              {file.seconds != null && (
                <span className="numeric" style={{ font: "700 10px 'Space Grotesk',sans-serif", color: 'var(--sub)', flex: 'none' }}>
                  {clipLength(file.seconds)}
                </span>
              )}
            </span>
          ) : (
            // A drawing or a PDF. It opens in a new tab rather than in a viewer
            // written here: the browser's PDF reader can search, rotate and
            // print, and none of that is worth reimplementing badly.
            <a
              key={file.id}
              href={andonApi.attachmentUrl(alertId, file.id)}
              target="_blank"
              rel="noreferrer noopener"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 11,
                padding: '11px 14px',
                borderRadius: 16,
                background: mine ? 'var(--surface2)' : 'var(--surface)',
                border: mine ? 'none' : '1px solid var(--line)',
                color: 'var(--ink)',
                textDecoration: 'none',
                maxWidth: 250,
              }}
            >
              <span style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--bg)', display: 'grid', placeItems: 'center', flex: 'none', color: 'var(--sub)' }}>
                <Icon name="file" size={17} />
              </span>
              <span style={{ minWidth: 0 }}>
                {/* The name wraps at the ellipsis rather than widening the
                    bubble — an unwrapped filename is what made the thread
                    scroll sideways before. */}
                <span style={{ display: 'block', font: '600 12px Manrope,sans-serif', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file.name}
                </span>
                <span style={{ display: 'block', font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)' }}>{fileSize(file.bytes)}</span>
              </span>
            </a>
          ),
        )}
      </span>

      {viewing && (
        <Lightbox alertId={alertId} items={pictures} startAt={Math.max(0, pictures.findIndex((p) => p.id === viewing.id))} onClose={() => setViewing(null)} />
      )}
    </>
  );
}

/**
 * WHICH EVENTS ARE LOUD.
 *
 * Only escalations, and only because an escalation already means amber
 * everywhere else: the card goes amber, the tier badge is amber. An event
 * that says the ladder climbed is the one line on a thread somebody scrolling
 * past needs to catch. Keyed on the TYPE rather than on the words, so it
 * survives a language pack.
 */
const LOUD_EVENTS = new Set(['escalated']);

/**
 * Something that happened, as a line across the middle.
 *
 * 12.5px, not 10px: the old size was chosen for a tidy thread and paid for by
 * the longest lines, which are the ones that matter most — the escalation
 * notes. THE RULES KEEP A FLOOR OF THEIR OWN so they cannot be squeezed to
 * nothing by a long sentence, and `minWidth: 0` on the label is what actually
 * lets it shrink; without it wrapping never gets the chance to happen and the
 * thread scrolls sideways.
 */
function Event({ event }: { event: AlertEventVM }) {
  const loud = event.type != null && LOUD_EVENTS.has(event.type);
  const rule = <span aria-hidden="true" style={{ flex: '1 1 12px', minWidth: 12, height: 1, background: loud ? 'var(--warn)' : 'var(--line)', opacity: loud ? 0.45 : 1 }} />;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '4px 0' }}>
      {rule}
      <span style={{ flex: '0 1 auto', minWidth: 0, font: '500 12.5px/1.5 Manrope,sans-serif', textAlign: 'center', overflowWrap: 'anywhere' }}>
        {event.lead && <span style={{ font: '700 12.5px/1.5 Manrope,sans-serif', color: loud ? 'var(--warn)' : 'var(--ink)' }}>{event.lead}</span>}
        {event.lead && event.detail ? ' ' : null}
        {event.detail && <span style={{ color: 'var(--sub)' }}>{event.detail}</span>}
      </span>
      {rule}
    </div>
  );
}

function Divider({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '4px 0' }}>
      <span aria-hidden="true" style={{ flex: '1 1 12px', minWidth: 12, height: 1, background: 'var(--line)' }} />
      <span style={{ flex: '0 1 auto', minWidth: 0, font: '600 10px/1.5 Manrope,sans-serif', color: 'var(--sub)', textAlign: 'center', overflowWrap: 'anywhere' }}>{text}</span>
      <span aria-hidden="true" style={{ flex: '1 1 12px', minWidth: 12, height: 1, background: 'var(--line)' }} />
    </div>
  );
}
